/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { safeLocalStorage } from "@/helper/safeLocalStorage";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import {
  CONSENT_KEY,
  PREFS_KEY,
  CURRENT_POLICY_VERSION,
  type ConsentRecord,
  readCookiePrefs,
} from "@/utils/cookiePrefs";
import { useFocusTrap } from "@/hooks/useFocusTrap";

/**
 * GDPR cookie consent banner with granular preferences.
 *
 * Only Google Analytics is currently active. The preferences panel shows:
 *   - Essential cookies (always on)
 *   - Analytics toggle (Google Analytics)
 *
 * Banner view: Accept All | Manage Preferences | Decline
 * Preferences view: toggles + Save / Accept All / Decline All
 *
 * The footer "Cookie Settings" button dispatches "ds:show-cookie-preferences"
 * which re-opens in preferences mode without a page reload.
 *
 * Storage:
 *   ds_cookie_prefs    — JSON: ConsentRecord (analytics, timestamp, policyVersion, source)
 *   ds_cookie_consent  — legacy "accepted"/"declined" (backward compat write)
 *
 * GDPR compliance:
 *   - D-01: Stores timestamp + policy version for auditable consent records.
 *   - D-02: Sends gtag consent update to stop GA4 immediately when user withdraws.
 *
 * Accessibility:
 *   - The preferences panel is a true modal dialog (role="dialog", aria-modal="true").
 *   - Focus is trapped inside the panel while it is open (WCAG 2.1.2 Level A).
 *   - Escape key closes the panel and returns focus to the trigger element.
 *   - On open, focus moves to the first interactive element (analytics toggle).
 */

type ConsentSource = ConsentRecord["source"];

function applyAndSavePrefs(analytics: boolean, source: ConsentSource): void {
  const record: ConsentRecord = {
    analytics,
    source,
    timestamp: new Date().toISOString(),
    policyVersion: CURRENT_POLICY_VERSION,
  };

  safeLocalStorage.setItem(PREFS_KEY, JSON.stringify(record));
  safeLocalStorage.setItem(CONSENT_KEY, analytics ? "accepted" : "declined");

  if (analytics) {
    window.__ds_load_analytics?.({ analytics: true });
  } else {
    // D-02: Stop GA4 immediately if it is already running this session.
    // This call is a no-op if GA4 was never loaded (user declined from the start).
    window.gtag?.("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
}

type View = "banner" | "preferences";

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<View | null>(null);
  const [analyticsOn, setAnalyticsOn] = useState(false);

  // Refs for focus trap: the preferences panel container and the element that
  // triggered the panel (so focus returns to it when the panel closes).
  const prefsRef = useRef<HTMLDivElement>(null);
  const manageButtonRef = useRef<HTMLButtonElement>(null);

  // Activate focus trap only while the preferences panel is showing.
  // Escape closes the panel and returns focus to the trigger button.
  const handleClosePrefs = useCallback((): void => {
    setView(null);
  }, []);

  useFocusTrap(
    view === "preferences",
    prefsRef,
    handleClosePrefs,
    manageButtonRef,
  );

  useEffect(() => {
    if (!readCookiePrefs()) {
      setView("banner");
    }
  }, []);

  useEffect(() => {
    const handler = (): void => {
      const existing = readCookiePrefs();
      setAnalyticsOn(existing?.analytics ?? false);
      setView("preferences");
    };
    window.addEventListener("ds:show-cookie-preferences", handler);
    return () =>
      window.removeEventListener("ds:show-cookie-preferences", handler);
  }, []);

  const handleAcceptAll = useCallback((): void => {
    applyAndSavePrefs(true, "banner_accept");
    setView(null);
  }, []);

  const handleDeclineAll = useCallback((): void => {
    applyAndSavePrefs(false, "banner_decline");
    setView(null);
  }, []);

  const handleSavePrefs = useCallback((): void => {
    applyAndSavePrefs(analyticsOn, "settings_update");
    setView(null);
  }, [analyticsOn]);

  const openPreferences = useCallback((): void => {
    const existing = readCookiePrefs();
    setAnalyticsOn(existing?.analytics ?? false);
    setView("preferences");
  }, []);

  if (!view) return null;

  // ── Preferences panel ────────────────────────────────────────────────────
  if (view === "preferences") {
    return (
      <div
        ref={prefsRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("cookie_consent.preferences_title")}
        className="cookie-consent-banner cookie-consent-banner--prefs"
      >
        <div className="cookie-consent-prefs-inner">
          <h2 className="cookie-consent-prefs-heading">
            {t("cookie_consent.preferences_title")}
          </h2>
          <p className="cookie-consent-prefs-intro">
            {t("cookie_consent.preferences_intro")}{" "}
            <Link
              to={AddLangToPath("/privacy-policy")}
              className="cookie-consent-link"
            >
              {t("cookie_consent.privacy_link")}
            </Link>
            {"."}
          </p>

          {/* Essential — always on */}
          <div className="cookie-pref-row">
            <div className="cookie-pref-info">
              <span className="cookie-pref-title">
                {t("cookie_consent.essential_title")}
              </span>
              <span className="cookie-pref-desc">
                {t("cookie_consent.essential_description")}
              </span>
            </div>
            <span className="cookie-pref-always-on">
              {t("cookie_consent.always_on")}
            </span>
          </div>

          {/* Analytics — Google Analytics */}
          <div className="cookie-pref-row">
            <div className="cookie-pref-info">
              <span className="cookie-pref-title">
                {t("cookie_consent.analytics_title")}
              </span>
              <span className="cookie-pref-desc">
                {t("cookie_consent.analytics_description")}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={analyticsOn}
              className={`cookie-pref-toggle${analyticsOn ? " cookie-pref-toggle--on" : ""}`}
              onClick={() => setAnalyticsOn((prev) => !prev)}
              aria-label={t("cookie_consent.analytics_title")}
            >
              <span className="cookie-pref-toggle-thumb" />
            </button>
          </div>

          <div className="cookie-consent-prefs-actions">
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn--accept"
              onClick={handleAcceptAll}
            >
              {t("cookie_consent.accept_all")}
            </button>
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn--save"
              onClick={handleSavePrefs}
            >
              {t("cookie_consent.save_preferences")}
            </button>
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn--decline"
              onClick={handleDeclineAll}
            >
              {t("cookie_consent.decline_all")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main banner ──────────────────────────────────────────────────────────
  return (
    <div
      role="region"
      aria-label={t("cookie_consent.title")}
      className="cookie-consent-banner"
    >
      <div className="cookie-consent-content">
        <p className="cookie-consent-text">
          <strong>{t("cookie_consent.title")}.</strong>{" "}
          {t("cookie_consent.body")}{" "}
          <Link
            to={AddLangToPath("/privacy-policy")}
            className="cookie-consent-link"
          >
            {t("cookie_consent.privacy_link")}
          </Link>
          {"."}
        </p>
        <div className="cookie-consent-actions">
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-btn--accept"
            onClick={handleAcceptAll}
          >
            {t("cookie_consent.accept_all")}
          </button>
          <button
            ref={manageButtonRef}
            type="button"
            className="cookie-consent-btn cookie-consent-btn--manage"
            onClick={openPreferences}
          >
            {t("cookie_consent.manage_preferences")}
          </button>
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-btn--decline"
            onClick={handleDeclineAll}
          >
            {t("cookie_consent.decline_all")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
